<template>
  <BottomSheetModal :modelValue="modelValue" auto-height hide-confirm @update:modelValue="onUpdate" @close="onClose">
    <template #title>
      <div class="mailing-title">
        <span>{{ t('mailing.title') }}</span>
      </div>
    </template>

    <div class="mailing-body">
      <!-- Idle / form -->
      <template v-if="!result">
        <p class="mailing-desc">{{ t('mailing.description') }}</p>

        <label class="mailing-label">{{ t('mailing.returnLinkLabel') }}</label>
        <v-text-field
          v-model="returnUrl"
          :placeholder="t('mailing.returnLinkPlaceholder')"
          variant="outlined"
          density="comfortable"
          hide-details
          clearable
          class="mb-1"
        />
        <p class="mailing-hint">{{ t('mailing.returnLinkHint') }}</p>

        <div class="mailing-switch-row">
          <div class="mailing-switch-text">
            <span class="mailing-switch-title">{{ t('mailing.testModeTitle') }}</span>
            <span class="mailing-switch-sub">{{ t('mailing.testModeSub', { email: TEST_EMAIL }) }}</span>
          </div>
          <v-switch v-model="testMode" color="primary" hide-details density="compact" inset />
        </div>

        <v-alert v-if="!testMode" type="warning" variant="tonal" density="compact" class="mb-3">
          {{ t('mailing.realWarning') }}
        </v-alert>

        <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mb-3">
          {{ errorMsg }}
        </v-alert>

        <button class="mailing-send-btn" :class="{ 'is-test': testMode }" :disabled="loading" @click="send">
          <v-progress-circular v-if="loading" indeterminate size="18" width="2" class="mr-2" />
          <v-icon v-else size="20" class="mr-2">mdi-email-fast-outline</v-icon>
          {{ testMode ? t('mailing.sendTest') : t('mailing.sendAll') }}
        </button>
      </template>

      <!-- Result -->
      <template v-else>
        <div class="mailing-result">
          <v-icon size="48" color="success" class="mb-3">mdi-check-circle-outline</v-icon>
          <h3 class="mailing-result-title">{{ t('mailing.sentTitle') }}</h3>
          <p class="mailing-result-sub">
            {{ result.testMode ? t('mailing.sentTestNote', { email: TEST_EMAIL }) : t('mailing.sentAllNote') }}
          </p>
          <div class="mailing-stats">
            <div class="mailing-stat"><span class="mailing-stat-num">{{ result.stats.sent }}</span><span class="mailing-stat-label">{{ t('mailing.statSent') }}</span></div>
            <div class="mailing-stat"><span class="mailing-stat-num">{{ result.stats.skipped }}</span><span class="mailing-stat-label">{{ t('mailing.statSkipped') }}</span></div>
            <div class="mailing-stat"><span class="mailing-stat-num">{{ result.stats.failed }}</span><span class="mailing-stat-label">{{ t('mailing.statFailed') }}</span></div>
          </div>
          <button class="mailing-send-btn" @click="onClose">{{ t('close') }}</button>
        </div>
      </template>
    </div>
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import BottomSheetModal from '@/components/BottomSheetModal.vue'
import axiosInstance from '@/services/axiosInstance'

const props = defineProps<{
  modelValue: boolean
  /** Article urlTitle (slug) */
  urlTitle: string | null
}>()
const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void
}>()

const { t } = useI18n({ useScope: 'global' })
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'
// Musí odpovídat MAILING_TEST_EMAIL na serveru.
const TEST_EMAIL = 'mir.skala@gmail.com'

interface SendResult {
  testMode: boolean
  stats: { total: number; sent: number; skipped: number; failed: number }
}

const returnUrl = ref('')
const testMode = ref(true)
const loading = ref(false)
const errorMsg = ref<string | null>(null)
const result = ref<SendResult | null>(null)

// Reset při otevření.
watch(() => props.modelValue, (open) => {
  if (open) {
    returnUrl.value = ''
    testMode.value = true
    loading.value = false
    errorMsg.value = null
    result.value = null
  }
})

function onUpdate(v: boolean) {
  emit('update:modelValue', v)
}
function onClose() {
  emit('update:modelValue', false)
}

async function send() {
  if (!props.urlTitle || loading.value) return
  loading.value = true
  errorMsg.value = null
  try {
    const { data } = await axiosInstance.post(
      `${API_BASE_URL}/mailing/articles/${encodeURIComponent(props.urlTitle)}/send`,
      { returnUrl: returnUrl.value?.trim() || undefined, testMode: testMode.value },
    )
    result.value = { testMode: !!data.testMode, stats: data.stats }
  } catch (err: any) {
    errorMsg.value = err?.response?.data?.message || t('mailing.error')
    console.error('[mailing] send failed:', err?.response?.data || err)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.mailing-title { font-weight: 600; }
.mailing-body { padding-top: 4px; }
.mailing-desc {
  color: rgba(var(--v-theme-sheetText), 0.7);
  font-size: 0.9rem;
  margin: 0 0 18px;
  line-height: 1.5;
}
.mailing-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: rgb(var(--v-theme-sheetText));
  margin-bottom: 6px;
}
.mailing-hint {
  font-size: 0.78rem;
  color: rgba(var(--v-theme-sheetText), 0.55);
  margin: 0 0 18px;
  line-height: 1.4;
}
.mailing-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  border-bottom: 1px solid rgba(var(--v-theme-sheetText), 0.1);
  margin-bottom: 16px;
}
.mailing-switch-text { display: flex; flex-direction: column; }
.mailing-switch-title { font-size: 0.9rem; font-weight: 600; color: rgb(var(--v-theme-sheetText)); }
.mailing-switch-sub { font-size: 0.78rem; color: rgba(var(--v-theme-sheetText), 0.55); }
.mailing-send-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
  background: rgb(var(--v-theme-error));
  border: none;
  cursor: pointer;
  transition: filter 0.2s ease, transform 0.1s ease;
}
.mailing-send-btn.is-test { background: rgb(var(--v-theme-primary)); }
.mailing-send-btn:active { transform: scale(0.98); }
.mailing-send-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.mailing-result { text-align: center; padding: 12px 0; }
.mailing-result-title { font-size: 1.2rem; font-weight: 700; color: rgb(var(--v-theme-sheetText)); margin: 0 0 6px; }
.mailing-result-sub { font-size: 0.85rem; color: rgba(var(--v-theme-sheetText), 0.6); margin: 0 0 20px; }
.mailing-stats { display: flex; justify-content: center; gap: 28px; margin-bottom: 24px; }
.mailing-stat { display: flex; flex-direction: column; align-items: center; }
.mailing-stat-num { font-size: 1.6rem; font-weight: 700; color: rgb(var(--v-theme-sheetText)); }
.mailing-stat-label { font-size: 0.75rem; color: rgba(var(--v-theme-sheetText), 0.55); text-transform: uppercase; letter-spacing: 0.5px; }
</style>
